import { makeStyles } from '@material-ui/core';
import api from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { BoxWrapper, Button, Loader, Modal } from 'components/Base';
import Forms from 'components/Forms';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomerType } from 'store/modules/Customers/types';
import { PolicyType } from 'store/modules/Policies/types';
import { errHelper, Notify } from 'utils';
import ImportPolicyRow from './ImportPolicyRow';
import { findError } from 'components/Forms/Utils/findError';

interface IoImportPoliciesProps {
  isModalOpen: boolean;
  customers: Array<CustomerType>;
  closeModal: Function;
  setIncompletePolices: Function;
  setCompletePolicies: Function;
  setExistingPolicies: Function;
  isImportingPolicies: boolean;
}

const useStyles = makeStyles({
  container: {
    width: '1000px',
    minHeight: '500px',
    maxHeight: '557px',
    position: 'relative',
    paddingBottom: '112px',
    overflow: 'auto',
    borderBottomLeftRadius: '25px',
    borderBottomRightRadius: '25px'
  },
  row: {
    paddingLeft: '36px',
    paddingRight: '36px',
    paddingTop: '12px',
    paddingBottom: '12px',
    fontWeight: 700,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    borderBottom: '1px solid #eaeaea',
    borderTop: '1px solid #eaeaea',
    marginTop: '16px'
  },
  rowItem: {
    width: '333px'
  },
  titleRowItem: {
    width: '200px',
    color: '#004258'
  },
  policyRow: {
    paddingLeft: '36px',
    paddingRight: '36px',
    paddingTop: '12px',
    paddingBottom: '12px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  policyItem: {
    width: '200px'
  },
  buttonContainer: {
    width: '100%',
    height: '112px',
    backgroundColor: 'white',
    position: 'absolute',
    bottom: 0,
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignContent: 'center',
    alignItems: 'center',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    paddingRight: '36px'
  },
  noResults: {
    width: '100%',
    padding: '2rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    textAlign: 'center'
  }
});

const IoImportPolicies = ({
  isModalOpen,
  customers,
  closeModal,
  setIncompletePolices,
  setCompletePolicies,
  setExistingPolicies,
  isImportingPolicies
}: IoImportPoliciesProps) => {
  const classes = useStyles();
  const directCustomers = customers.filter(customer => !customer.is_jointed);
  const [policyData, setPolicyData] = useState({} as any);
  const { portfolio } = useParams() as any;
  const [selectedPolices, setSelectedPolicies] = useState([] as PolicyType[]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const wrapExceptions = [
    'wrap',
    'trading account',
    'cash account pension',
    'wrap cash',
    'reserve account',
    'pending trades'
  ];

  const addSelectedPolicy = (policy: PolicyType) => {
    const polices = [...selectedPolices, policy];
    setSelectedPolicies(polices);
  };

  const removeSelectedPolicy = (policy: PolicyType) => {
    const polices = selectedPolices.filter(sp => sp.io_id !== policy.id);
    setSelectedPolicies(polices);
  };

  useEffect(() => {
    if (isImportingPolicies) {
      setIsLoading(true);
      api.intelifloApi
        .importPolicies(
          customers.length === 1
            ? { first_client_id: directCustomers[0].intelligent_office_id }
            : {
                first_client_id: directCustomers[0].intelligent_office_id,
                second_client_id: directCustomers[1].intelligent_office_id
              }
        )
        .then((response: AxiosResponse) => {
          const sortedPolicies = response.data.reduce(
            (acc: any, policy: any) => {
              // I will delete this next condition after testing is done as it can be difficult to find clients
              // with inactive funds
              const isValid = !!policy.fund_allocations.find(
                (fund: any) => fund.fund?.active
              );
              const filterWrapValues = policy.fund_allocations.filter(
                // eslint-disable-next-line array-callback-return
                (f: any) => {
                  if (!wrapExceptions.includes(f.fund.name.toLowerCase())) {
                    return f;
                  } else {
                    policy.wrap_amount = `${
                      Number(policy.wrap_amount) + Number(f.fund_value)
                    }`;
                  }
                }
              );
              if (policy.wrap_amount === '0.000') policy.wrap_amount = null;
              policy.fund_allocations = filterWrapValues;
              policy.isValid = isValid;
              if (acc[policy.owner.id] && policy.fund_value !== '0') {
                acc[policy.owner.id].push(policy);
              } else if (!acc[policy.owner.id] && policy.fund_value !== '0') {
                acc[policy.owner.id] = [policy];
              }
              return acc;
            },
            {}
          );
          setPolicyData(sortedPolicies);
          setIsLoading(false);
        })
        .catch((err: AxiosError) => {
          errHelper(err);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImportingPolicies]);

  const savePolicies = (polices: any[]) => {
    const preparedPolicy = (policy: any) =>
      Forms.Utils.prepareSubmit.IoPolicies(policy);
    return Promise.all(
      polices.map(p =>
        api.addExistingPolicy({ portfolio, policy: preparedPolicy(p) })
      )
    )
      .then(() => {
        return api.getExistingPolicies({ portfolio }).then(({ data }: any) => {
          setExistingPolicies(data);
          Notify.success(
            'Existing policies saved',
            'New Policies were successfully added'
          );
          return data;
        });
      })
      .catch(err => {
        if (err.response.status === 400) {
          const { key, error } = findError(err.response.data);
          const readableKey = key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c: string) => c.toUpperCase());

          Notify.error(
            'Could not add policy',
            `Field: ${readableKey}, Error: ${error}`
          );
        } else {
          Notify.error(err.response.data.message);
        }
      });
  };
  const checkFundAllocations = (fundAllocations: Array<any>) => {
    const fundsWithAllocations = fundAllocations.filter(
      (fund: any) => fund.allocation !== null
    );

    if (fundsWithAllocations.length) {
      return (
        fundAllocations.reduce((total: number, fund: any) => {
          total += Number(fund.allocation);
          return total;
        }, 0) === 1
      );
    }

    return true;
  };
  const filterPolicies = async () => {
    const jointCustomerId = customers.find(customer => customer.is_jointed)?.id;

    setIsProcessing(true);
    // eslint-disable-next-line array-callback-return
    const actvivePolices = selectedPolices
      .filter(policy => {
        const active = !policy.fund_allocations.find(
          fund => !fund.fund?.active
        );
        const valid = checkFundAllocations(policy.fund_allocations);
        if (active && valid) return policy;
      })
      .map(policy => {
        if (policy.owner.is_jointed && jointCustomerId) {
          policy.owner.id = jointCustomerId;
        }

        return policy;
      });
    const newSaved = await savePolicies(actvivePolices);
    // eslint-disable-next-line array-callback-return
    const inactivePolicies = selectedPolices.filter(policy => {
      const inactive = !!policy.fund_allocations.find(
        fund => !fund.fund?.active
      );
      const valid = checkFundAllocations(policy.fund_allocations);
      if (inactive || !valid) return policy;
    });
    setIncompletePolices(inactivePolicies);
    setCompletePolicies(newSaved.results);
    setIsProcessing(false);
    closeModal();
  };

  return (
    <Modal isOpen={isModalOpen} closeModalFunc={() => closeModal()}>
      <BoxWrapper
        title="Importing policies of your client"
        closeModal={() => closeModal()}
      >
        <div className={classes.container}>
          {directCustomers &&
            directCustomers.length > 0 &&
            directCustomers.map((customer: CustomerType, index: number) => {
              return (
                <React.Fragment key={customer.id}>
                  <div className={classes.row}>
                    <div className={classes.rowItem}>
                      Client{index + 1}: {customer.display_name}
                    </div>
                    <div className={classes.rowItem}>
                      IO ID: {customer.intelligent_office_id}
                    </div>
                  </div>
                  <div className={classes.row}>
                    <div className={classes.titleRowItem}>Select</div>
                    <div className={classes.titleRowItem}>Policy Type</div>
                    <div className={classes.titleRowItem}>Provider</div>
                    <div className={classes.titleRowItem}>Platform</div>
                    <div className={classes.titleRowItem}>Joint Policy</div>
                  </div>
                  <Loader isLoading={isLoading} size={24}>
                    {!!policyData && !!policyData[customer.id] ? (
                      policyData[customer.id].map(
                        (policy: any, index: number) => {
                          return (
                            <ImportPolicyRow
                              key={policy.id}
                              policy={policy}
                              index={index}
                              addSelectedPolicy={addSelectedPolicy}
                              removeSelectedPolicy={removeSelectedPolicy}
                            />
                          );
                        }
                      )
                    ) : (
                      <div className={classes.noResults}>
                        <div>No results</div>
                      </div>
                    )}
                  </Loader>
                </React.Fragment>
              );
            })}
        </div>
        <div className={classes.buttonContainer}>
          <Button
            isLoading={isProcessing}
            onClick={() => {
              filterPolicies();
            }}
            key="Import Policies"
            type="submit"
          >
            Import Policies
          </Button>
        </div>
      </BoxWrapper>
    </Modal>
  );
};

export default IoImportPolicies;
